import {
  formatRange,
  isCurrentDay,
  mondayOf,
  parseDateParam,
  rangeDays,
  shiftDays,
  toISODate,
} from "@calendar/weekDates";
import { WeekView } from "@calendar/WeekView";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { CompanionHeader } from "@components/CompanionHeader";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  cn,
  IconButton,
  PanelError,
  SegmentedControl,
} from "@scribenote/brian-design-system";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState } from "react";

const dayCountForWidth = (width: number): 1 | 3 | 7 => {
  if (width === 0) return 7;
  if (width < 640) return 1;
  if (width < 1024) return 3;
  return 7;
};

const DAY_OPTIONS: { value: 1 | 3 | 7; label: string }[] = [
  { value: 1, label: "Day" },
  { value: 3, label: "3-Day" },
  { value: 7, label: "Week" },
];

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

type FlipDirection = "prev" | "next";

/** The page currently clipped to the pad; becomes the flip overlay on change. */
interface PageSnapshot {
  key: string;
  anchorKey: string;
  anchor: Date;
  dayCount: number;
}

/**
 * The calendar as a week pad: a paper sheet resting on the page (WEB-3328).
 * The controls live on the pad's header strip; the schedule below is the pad's
 * page, and navigating weeks turns that page.
 */
export const CalendarShell = () => {
  const { week, days } = useSearch({ from: "/calendar" });
  const navigate = useNavigate();

  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  // Explicit `?days=` choice wins; otherwise fall back to a width-based default.
  const dayCount = days ?? dayCountForWidth(width);
  // A full week snaps to Monday so it always reads Mon–Sun; narrower layouts
  // anchor on the param date itself and step by however many days are shown.
  const anchor =
    dayCount === 7 ? mondayOf(parseDateParam(week)) : parseDateParam(week);
  const anchorKey = toISODate(anchor);
  const visibleDays = rangeDays(anchor, dayCount);
  const contentKey = `${anchorKey}:${dayCount}`;

  /*
   * The page flip. The schedule area holds two layers: the keyed incoming
   * page (remounts on contentKey change, replaying its settle animation) and
   * a decorative outgoing overlay rendering the page that was showing. The
   * direction is derived from date order at flip time - `next` lifts the
   * outgoing page up off the pad, `prev` lowers it back down on top. Under
   * prefers-reduced-motion the overlay is never mounted, so the swap is
   * instant and nothing can strand. State-during-render is the sanctioned
   * "storing information from previous renders" pattern.
   */
  const [page, setPage] = useState<PageSnapshot>({
    key: contentKey,
    anchorKey,
    anchor,
    dayCount,
  });
  const [flip, setFlip] = useState<FlipDirection | null>(null);
  const [outgoing, setOutgoing] = useState<
    (PageSnapshot & { direction: FlipDirection }) | null
  >(null);
  if (page.key !== contentKey) {
    const direction: FlipDirection =
      anchorKey < page.anchorKey ? "prev" : "next";
    setPage({ key: contentKey, anchorKey, anchor, dayCount });
    // A density-only change (Day/3-Day/Week) re-rules the pad rather than
    // turning a page: the incoming settle plays, but no page lifts away.
    setFlip(direction);
    if (anchorKey !== page.anchorKey && !prefersReducedMotion()) {
      setOutgoing({ ...page, direction });
    }
  }

  const goTo = (target: Date) => {
    const normalized = dayCount === 7 ? mondayOf(target) : target;
    navigate({
      to: "/calendar",
      search: { week: toISODate(normalized), days },
    });
  };

  // Dropping out of Week view should land on today (if it's in the visible
  // week) rather than carrying over the Monday-snapped week anchor.
  const setDays = (value: 1 | 3 | 7) => {
    const targetKey =
      dayCount === 7 && value !== 7 && visibleDays.some(isCurrentDay)
        ? toISODate(new Date())
        : anchorKey;
    navigate({ to: "/calendar", search: { week: targetKey, days: value } });
  };

  return (
    <div
      ref={rootRef}
      className="flex h-screen w-screen flex-col bg-paper text-ink-body"
    >
      <CompanionHeader back={{ title: "Calendar", testId: "calendar-back" }} />

      <main
        aria-label="Calendar"
        className="flex min-h-0 min-w-0 flex-1 flex-col p-2 sm:p-4"
      >
        {/* The week pad: the sheet the schedule is written on, resting on the
            paper page. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col rounded-sheet bg-card shadow-lift-lg">
          <nav
            aria-label="Calendar controls"
            className="grid grid-cols-1 items-center gap-2 border-b border-rule px-2 py-2.5 sm:grid-cols-2 sm:px-4 sm:py-3 lg:grid-cols-[1fr_auto_1fr]"
          >
            <div className="order-2 flex justify-center sm:justify-start lg:order-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goTo(new Date())}
                data-testid="calendar-this-week"
              >
                Today
              </Button>
            </div>

            <div className="order-1 flex items-center justify-center gap-1.5 sm:col-span-2 lg:order-2 lg:col-span-1">
              <IconButton
                variant="outline"
                size="sm"
                aria-label="Previous"
                onClick={() => goTo(shiftDays(anchor, -dayCount))}
                data-testid="calendar-prev-week"
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              </IconButton>
              <div className="min-w-40 text-center font-display text-base font-medium text-ink sm:min-w-56">
                {formatRange(visibleDays[0], visibleDays[dayCount - 1])}
              </div>
              <IconButton
                variant="outline"
                size="sm"
                aria-label="Next"
                onClick={() => goTo(shiftDays(anchor, dayCount))}
                data-testid="calendar-next-week"
              >
                <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
              </IconButton>
            </div>

            <div className="order-3 flex items-center justify-center sm:justify-end">
              <SegmentedControl
                aria-label="Days shown"
                options={DAY_OPTIONS}
                value={dayCount}
                onValueChange={setDays}
                size="md"
                variant="outline"
              />
            </div>
          </nav>

          {/* The pad's page. Both flip layers live here; overflow-hidden keeps
              the turning page inside the pad. */}
          <div className="relative min-h-0 flex-1 overflow-hidden rounded-b-sheet">
            {/* bg-card on the page itself (not just the pad behind it) is what
                lets the `prev` page land as an opaque sheet over the outgoing
                one instead of blending with it. */}
            <div
              key={contentKey}
              className={cn(
                "relative flex h-full min-h-0 flex-col bg-card",
                outgoing?.direction === "prev" && "z-10",
                flip !== null &&
                  !prefersReducedMotion() &&
                  (flip === "next"
                    ? "animate-week-flip-in-next"
                    : "animate-week-flip-in-prev"),
              )}
            >
              <AsyncBoundary
                skeleton={<WeekSkeleton dayCount={dayCount} />}
                fallback={(retry) => (
                  <PanelError label="calendar" onRetry={retry} />
                )}
                resetKeys={[anchorKey, dayCount]}
              >
                <WeekView anchor={anchor} dayCount={dayCount} />
              </AsyncBoundary>
            </div>

            {outgoing && (
              <div
                aria-hidden="true"
                inert
                onAnimationEnd={(e) => {
                  // Child animations bubble; only the overlay's own flip ends it.
                  if (e.animationName.startsWith("week-flip-out")) {
                    setOutgoing(null);
                  }
                }}
                className={cn(
                  "pointer-events-none absolute inset-0 flex min-h-0 flex-col bg-card",
                  outgoing.direction === "next"
                    ? "z-10 animate-week-flip-out-next"
                    : "animate-week-flip-out-prev",
                )}
              >
                <AsyncBoundary
                  skeleton={<WeekSkeleton dayCount={outgoing.dayCount} />}
                  fallback={() => null}
                >
                  <WeekView
                    anchor={outgoing.anchor}
                    dayCount={outgoing.dayCount}
                  />
                </AsyncBoundary>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// Plausible ghost-card placements (top/height as % of the column) so the
// loading grid looks populated and organic rather than empty.
const SKELETON_GHOSTS = [
  [
    { top: 12, height: 14 },
    { top: 55, height: 20 },
  ],
  [{ top: 30, height: 18 }],
  [
    { top: 8, height: 12 },
    { top: 40, height: 16 },
    { top: 70, height: 14 },
  ],
  [
    { top: 22, height: 22 },
    { top: 62, height: 12 },
  ],
  [{ top: 45, height: 18 }],
  [
    { top: 15, height: 16 },
    { top: 52, height: 24 },
  ],
  [{ top: 35, height: 20 }],
];

const WeekSkeleton = ({ dayCount }: { dayCount: number }) => (
  <div className="flex h-full flex-col">
    <div className="flex border-b border-rule">
      <div className="w-14 shrink-0" />
      {Array.from({ length: dayCount }).map((_, i) => (
        <div
          key={i}
          className="flex flex-1 flex-col items-center gap-1.5 px-1 py-2"
        >
          <div
            className="h-2.5 w-8 animate-pulse rounded-full bg-recess motion-reduce:animate-none"
            style={{ animationDelay: `${i * 70}ms` }}
          />
          <div
            className="h-7 w-7 animate-pulse rounded-full bg-recess motion-reduce:animate-none"
            style={{ animationDelay: `${i * 70 + 40}ms` }}
          />
        </div>
      ))}
    </div>

    <div className="flex min-h-0 flex-1">
      <div className="flex w-14 shrink-0 flex-col justify-between py-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="mr-1 ml-auto h-2 w-6 animate-pulse rounded-full bg-recess motion-reduce:animate-none"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
      </div>

      {Array.from({ length: dayCount }).map((_, col) => (
        <div key={col} className="relative flex-1 border-l border-rule">
          {Array.from({ length: 6 }).map((_, r) => (
            <div
              key={r}
              className="absolute inset-x-0 border-t border-rule"
              style={{ top: `${(r / 6) * 100}%` }}
            />
          ))}
          {SKELETON_GHOSTS[col % SKELETON_GHOSTS.length].map((g, gi) => (
            <div
              key={gi}
              className="absolute inset-x-1 animate-pulse overflow-hidden rounded-paper bg-recess motion-reduce:animate-none"
              style={{
                top: `${g.top}%`,
                height: `${g.height}%`,
                animationDelay: `${(col * 2 + gi) * 90}ms`,
              }}
            >
              <span className="absolute left-0 top-0 h-full w-1 bg-faint" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);
